import { Medium, FeedEntryType } from "protogen/advisors_service_pb";
import { Box, IconButton } from "@mui/material";
import { Mail, Phone, Search, Filter, Video } from "lucide-react";
import { FeedFocusState } from "../../types/feed";
import ButtonGroupSquare from "components/common/ButtonGroupSquare";
import SearchDialog from "components/search/SearchDialog";
import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import useIsMobile from "components/hooks/useIsMobile";
import CheckedDropDown from "components/common/CheckedDropDown";
import { ActivityFeedFilterContext } from "components/context/ActivityFeedFilterContext";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdmin } from "common/userUtils";
import { useCreateVideoRoom } from "services/video";

const FilterDropdown = () => {
  const { entryType, updateEntryType } = useContext(ActivityFeedFilterContext);
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = isUserAdmin(currentUser);
  const isMobile = useIsMobile();

  return (
    <CheckedDropDown
      noOutline={!isMobile}
      sx={{
        borderRadius: "100px !important",
        "& .MuiInputBase-root": {
          borderRadius: "100px !important",
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: "100px !important",
        },
        "& .MuiSelect-root": {
          borderRadius: "100px !important",
        },
      }}
      radio={true}
      disabled={false}
      displayValue={
        <IconButton
          sx={{
            border: isMobile ? "1px solid #D4D4D4" : "none !important",
            padding: "8px !important",
            color: "#6B6E7B",
            backgroundColor: isMobile ? "#fff" : "transparent",
            width: "40px",
            height: "40px",
            minHeight: "40px",
            borderRadius: "50px",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
          size="small"
        >
          <Filter height="20px" width="20px" />
        </IconButton>
      }
      hasIcon={false}
      sxSelect={{
        "& .MuiInputBase-root": {
          borderRadius: "100px !important",
        },
        "& .MuiOutlinedInput-root": {
          minHeight: "unset !important",
          borderRadius: "100px !important",
          height: "40px",
        },
        ".MuiSelect-select.MuiSelect-outlined.MuiSelect-multiple": {
          padding: "0px",
          margin: "0px",
          height: "40px",
          display: "flex",
          alignItems: "center",
        },
        minHeight: "40px",
        height: "40px",
        padding: "0px",
      }}
      options={[
        {
          label: "All",
          value: FeedEntryType.UNSPECIFIED.toString(),
          defaultChecked: entryType === FeedEntryType.UNSPECIFIED,
          mutuallyExclusive: true,
        },
        {
          label: "Phone calls",
          value: FeedEntryType.PHONE_CALL.toString(),
          defaultChecked: entryType === FeedEntryType.PHONE_CALL,
          mutuallyExclusive: true,
        },
        {
          label: "Task activity",
          value: FeedEntryType.TASK_ACTIVITY.toString(),
          defaultChecked: entryType === FeedEntryType.TASK_ACTIVITY,
          mutuallyExclusive: true,
        },
        {
          label: "Messages",
          value: FeedEntryType.MESSAGE.toString(),
          defaultChecked: entryType === FeedEntryType.MESSAGE,
          mutuallyExclusive: true,
        },
        ...(isAdmin
          ? [
              {
                label: "Admin -Video calls",
                value: FeedEntryType.VIDEO_CALL.toString(),
                defaultChecked: entryType === FeedEntryType.VIDEO_CALL,
                mutuallyExclusive: true,
              },
            ]
          : []),
        ...(isAdmin
          ? [
              {
                label: "Admin - All",
                value: FeedEntryType.ADMIN_ALL.toString(),
                defaultChecked: entryType === FeedEntryType.ADMIN_ALL,
                mutuallyExclusive: true,
              },
            ]
          : []),
      ]}
      onChange={(r) => {
        if (r.length === 0) {
          updateEntryType(FeedEntryType.UNSPECIFIED);
          return;
        }
        const value = parseInt(r[0].value);
        updateEntryType(value as FeedEntryType);
      }}
    />
  );
};

type ContactSectionProps = {
  feedFocusState: FeedFocusState;
  setFeedFocusState: (s: FeedFocusState) => void;
  familyRef?: string;
};
export default ({
  feedFocusState,
  setFeedFocusState,
  familyRef,
}: ContactSectionProps) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = isUserAdmin(currentUser);
  const { request: createVideoRoom } = useCreateVideoRoom();

  const handleVideoCall = async () => {
    const response = await createVideoRoom({
      recordingEnabled: true,
      familyRef: familyRef || "",
    });

    if (response && response.videoRoom) {
      navigate(`/video/${response.videoRoom.uuid}`);
    }
  };

  // Create communication buttons array - only include video for admins
  const communicationButtons = [
    {
      icon: <Phone />,
      onClick: () => {
        setFeedFocusState({
          ...feedFocusState,
          selectedMedium: Medium.PHONE,
          phoneCallTab: "phone",
        });
      },
      "aria-label": "Make phone call",
    },
    ...(isAdmin
      ? [
          {
            icon: <Video />,
            onClick: handleVideoCall,
            "aria-label": "Start video call",
          },
        ]
      : []),
    {
      icon: <Mail />,
      onClick: () => {
        setFeedFocusState({
          ...feedFocusState,
          selectedMedium: Medium.EMAIL,
        });
      },
      "aria-label": "Send email",
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      {/* Mobile buttons */}
      {isMobile && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            gap: "8px",
          }}
        >
          <IconButton
            sx={{
              border: "1px solid #D4D4D4",
              borderRadius: "50px",
              height: "40px",
              width: "40px",
              padding: "8px",
              color: "#6B6E7B",
              backgroundColor: "#fff",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
            onClick={() => {
              setFeedFocusState({
                ...feedFocusState,
                selectedMedium: Medium.PHONE,
                phoneCallTab: "phone",
              });
            }}
            aria-label="Make phone call"
          >
            <Phone height="20px" width="20px" />
          </IconButton>

          {isAdmin && (
            <IconButton
              sx={{
                border: "1px solid #D4D4D4",
                borderRadius: "50px",
                height: "40px",
                width: "40px",
                padding: "8px",
                color: "#6B6E7B",
                backgroundColor: "#fff",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
              onClick={handleVideoCall}
              aria-label="Start video call"
            >
              <Video height="20px" width="20px" />
            </IconButton>
          )}

          <IconButton
            sx={{
              border: "1px solid #D4D4D4",
              borderRadius: "50px",
              height: "40px",
              width: "40px",
              padding: "8px",
              color: "#6B6E7B",
              backgroundColor: "#fff",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
            onClick={() => {
              setFeedFocusState({
                ...feedFocusState,
                selectedMedium: Medium.EMAIL,
              });
            }}
            aria-label="Send email"
          >
            <Mail height="20px" width="20px" />
          </IconButton>

          <FilterDropdown />

          <IconButton
            sx={{
              border: "1px solid #D4D4D4",
              borderRadius: "50px",
              height: "40px",
              width: "40px",
              padding: "8px",
              color: "#6B6E7B",
              backgroundColor: "#fff",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
            onClick={() => setSearchOpen(true)}
            aria-label="Search"
          >
            <Search height="20px" width="20px" />
          </IconButton>

          <SearchDialog
            open={searchOpen}
            onClose={() => setSearchOpen(false)}
          />
        </Box>
      )}

      {/* Desktop buttons */}
      {!isMobile && (
        <Box
          sx={{
            display: "flex",
            padding: "16px 16px",
            justifyContent: "space-between",
            width: "100%",
            gap: "12px",
          }}
        >
          <ButtonGroupSquare variant="desktop" items={communicationButtons} />

          <ButtonGroupSquare
            variant="desktop"
            items={[
              {
                component: <FilterDropdown />,
              },
              {
                icon: <Search />,
                onClick: () => setSearchOpen(true),
                "aria-label": "Search",
              },
            ]}
          />

          <SearchDialog
            open={searchOpen}
            onClose={() => setSearchOpen(false)}
          />
        </Box>
      )}
    </Box>
  );
};
